// Thin JSON client for the platform API. Every request is same-origin and carries the X-BMP header
// that the server requires for anything that changes data.

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function api<T = unknown>(method: Method, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'X-BMP': '1' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, { method, headers, credentials: 'same-origin', body: body === undefined ? undefined : JSON.stringify(body) });
  let data: unknown = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const detail = (data as { detail?: unknown } | null)?.detail;
    let message = res.statusText || 'Something went wrong';
    let code: string | undefined;
    if (typeof detail === 'string') message = detail;
    else if (Array.isArray(detail)) message = detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join('; ') || message;
    else if (detail && typeof detail === 'object') {
      message = (detail as { message?: string }).message ?? message;
      code = (detail as { code?: string }).code;
    }
    const err = new ApiError(message, res.status, code);
    if (res.status === 401 || code === 'mfa_enrollment_required') window.dispatchEvent(new CustomEvent('bmp:auth', { detail: err }));
    throw err;
  }
  return data as T;
}

export const get = <T,>(url: string) => api<T>('GET', url);
export const post = <T,>(url: string, body?: unknown) => api<T>('POST', url, body ?? {});
export const patch = <T,>(url: string, body: unknown) => api<T>('PATCH', url, body);
export const put = <T,>(url: string, body: unknown) => api<T>('PUT', url, body);
export const del = <T,>(url: string, body?: unknown) => api<T>('DELETE', url, body);
